import { z } from 'zod';

import {
  DestinationPageFaqInputSchema,
  DestinationPageSeoLinkInputSchema,
  DestinationPageReviewInputSchema,
} from './admin-website-destination-pages.js';
import { WebsitePublishStatusSchema } from './admin-website-pages.js';
import { UuidSchema, IsoDateTimeSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

export {
  DestinationPageFaqInputSchema as CategoryPageFaqInputSchema,
  DestinationPageSeoLinkInputSchema as CategoryPageSeoLinkInputSchema,
  DestinationPageReviewInputSchema as CategoryPageReviewInputSchema,
} from './admin-website-destination-pages.js';

export const CategoryPageBestSellerInputSchema = z.object({
  packageId: UuidSchema,
});

export const CategoryPageCircleDestinationInputSchema = z.object({
  destinationPageId: UuidSchema,
});

/**
 * 2026-08-01. One row of the category page's "Sorting" tab - the manual
 * display order of the destination tiles on that page.
 *
 * Ordering only: a destination page left out of the list is NOT hidden,
 * it just falls back to the automatic rule (long haul first, then short
 * haul, then unclassified). Sending `[]` = "fully automatic".
 */
export const CategoryPageDestinationOrderInputSchema = z.object({
  destinationPageId: UuidSchema,
});

const CoreFields = z.object({
  slug: z.string().min(1).max(160),
  title: z.string().min(1).max(200),
  heroHeading: z.string().max(200).nullish(),
  heroSubheading: z.string().max(300).nullish(),
  heroMediaId: UuidSchema.nullish(),
  heroVideoId: UuidSchema.nullish(),
  // No length cap - "Read More" article copy can run long (5,000+ words).
  aboutHtml: z.string().nullish(),
  metaTitle: z.string().max(200).nullish(),
  metaDescription: z.string().max(400).nullish(),
  metaKeywords: z.string().max(400).nullish(),
  ogImageId: UuidSchema.nullish(),
  status: WebsitePublishStatusSchema.default('DRAFT'),
});

const childArrays = {
  bestSellers: z.array(CategoryPageBestSellerInputSchema).max(12).default([]),
  circleDestinations: z.array(CategoryPageCircleDestinationInputSchema).max(24).default([]),
  // A big category (e.g. International) legitimately lists 100+ destinations
  // and the tab is a full manual list, so the cap is generous.
  destinationOrder: z.array(CategoryPageDestinationOrderInputSchema).max(300).default([]),
  faqs: z.array(DestinationPageFaqInputSchema).max(50).default([]),
  seoLinks: z.array(DestinationPageSeoLinkInputSchema).max(200).default([]),
  reviews: z.array(DestinationPageReviewInputSchema).max(50).default([]),
};

export const CreateWebsiteCategoryPageRequestSchema = CoreFields.extend(childArrays);
export type CreateWebsiteCategoryPageRequest = z.infer<
  typeof CreateWebsiteCategoryPageRequestSchema
>;

export const UpdateWebsiteCategoryPageRequestSchema = CoreFields.partial().extend({
  bestSellers: z.array(CategoryPageBestSellerInputSchema).max(12).optional(),
  circleDestinations: z.array(CategoryPageCircleDestinationInputSchema).max(24).optional(),
  destinationOrder: z.array(CategoryPageDestinationOrderInputSchema).max(300).optional(),
  faqs: z.array(DestinationPageFaqInputSchema).max(50).optional(),
  seoLinks: z.array(DestinationPageSeoLinkInputSchema).max(200).optional(),
  reviews: z.array(DestinationPageReviewInputSchema).max(50).optional(),
});
export type UpdateWebsiteCategoryPageRequest = z.infer<
  typeof UpdateWebsiteCategoryPageRequestSchema
>;

export const WebsiteCategoryPageListQuerySchema = PaginationQuerySchema.extend({
  status: WebsitePublishStatusSchema.optional(),
  /** See WebsiteDestinationPageListQuerySchema.incomplete. */
  incomplete: BooleanQueryParamSchema.optional(),
});
export type WebsiteCategoryPageListQuery = z.infer<typeof WebsiteCategoryPageListQuerySchema>;

export const WebsiteCategoryPageListItemSchema = z.object({
  id: UuidSchema,
  slug: z.string(),
  title: z.string(),
  status: WebsitePublishStatusSchema,
  publishedAt: IsoDateTimeSchema.nullable(),
  updatedAt: IsoDateTimeSchema,
});
export type WebsiteCategoryPageListItem = z.infer<typeof WebsiteCategoryPageListItemSchema>;

export const WebsiteCategoryPageListResponseSchema = paginatedResponseSchema(
  WebsiteCategoryPageListItemSchema,
);
export type WebsiteCategoryPageListResponse = z.infer<typeof WebsiteCategoryPageListResponseSchema>;

const bestSellerOut = CategoryPageBestSellerInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});
const circleDestinationOut = CategoryPageCircleDestinationInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});
const destinationOrderOut = CategoryPageDestinationOrderInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});
const faqOut = DestinationPageFaqInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});
const seoLinkOut = DestinationPageSeoLinkInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});
const reviewOut = DestinationPageReviewInputSchema.extend({
  id: UuidSchema,
  sortOrder: z.number().int(),
});

/**
 * 2026-08-01. One selectable row in the "Sorting" tab - every destination
 * page bound to this category, already in the order the website renders
 * it (saved manual order first, then the automatic haul rule). The tab
 * seeds its drag list from this so the operator starts from what is
 * actually live rather than an empty box.
 */
export const CategoryPageDestinationOptionSchema = z.object({
  destinationPageId: UuidSchema,
  title: z.string(),
  slug: z.string(),
  /** SHORT_HAUL / LONG_HAUL from the destination master; null = unclassified. */
  haul: z.enum(['SHORT_HAUL', 'LONG_HAUL']).nullable(),
  /** Active packages behind the tile. 0 = the website hides this tile today. */
  packageCount: z.number().int().nonnegative(),
  /** False when the destination page itself is draft/archived (also hidden). */
  isPublished: z.boolean(),
});
export type CategoryPageDestinationOption = z.infer<typeof CategoryPageDestinationOptionSchema>;

export const CategoryPageDestinationOptionsResponseSchema = z.array(
  CategoryPageDestinationOptionSchema,
);
export type CategoryPageDestinationOptionsResponse = z.infer<
  typeof CategoryPageDestinationOptionsResponseSchema
>;

export const WebsiteCategoryPageDetailSchema = z.object({
  id: UuidSchema,
  slug: z.string(),
  title: z.string(),
  heroHeading: z.string().nullable(),
  heroSubheading: z.string().nullable(),
  heroMediaId: UuidSchema.nullable(),
  heroVideoId: UuidSchema.nullable(),
  aboutHtml: z.string().nullable(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  metaKeywords: z.string().nullable(),
  ogImageId: UuidSchema.nullable(),
  status: WebsitePublishStatusSchema,
  publishedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  bestSellers: z.array(bestSellerOut),
  circleDestinations: z.array(circleDestinationOut),
  destinationOrder: z.array(destinationOrderOut),
  faqs: z.array(faqOut),
  seoLinks: z.array(seoLinkOut),
  reviews: z.array(reviewOut),
});
export type WebsiteCategoryPageDetail = z.infer<typeof WebsiteCategoryPageDetailSchema>;
